/**
 * Who you are, kept on this machine (RFC 0324 3a).
 *
 * There is no sign-in, so the person is made here the first time you comment
 * and kept in the window's storage. Every window on the desktop loads from the
 * same origin, so they share it and are the same person.
 */

import type { PersonEntry } from '@mod/core/model'
import { useSyncExternalStore } from 'react'

const KEY = 'mod_person'
const CHANGED = 'mod:person'

export function readLocalPerson(): PersonEntry | null {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return null
    const p = JSON.parse(raw) as Partial<PersonEntry>
    if (typeof p.id !== 'string' || !p.id) return null
    if (typeof p.name !== 'string' || !p.name.trim()) return null
    return {
      id: p.id,
      name: p.name,
      ...(typeof p.avatarUrl === 'string' && p.avatarUrl
        ? { avatarUrl: p.avatarUrl }
        : {}),
    }
  } catch {
    return null
  }
}

/**
 * Save your name and photo. Keeps the id you already have, so renaming
 * yourself later does not make you a second person.
 */
export function saveLocalPerson(input: {
  name: string
  avatarUrl?: string
}): PersonEntry {
  const existing = readLocalPerson()
  const person: PersonEntry = {
    id: existing?.id ?? `p-${crypto.randomUUID()}`,
    name: input.name.trim(),
    ...(input.avatarUrl ? { avatarUrl: input.avatarUrl } : {}),
  }
  try {
    localStorage.setItem(KEY, JSON.stringify(person))
    window.dispatchEvent(new CustomEvent(CHANGED))
  } catch {
    // No storage (a locked-down browser): the person lasts this page only.
  }
  return person
}

/** Call `fn` when this window or another one changes the saved person. */
export function onLocalPersonChange(fn: () => void): () => void {
  const onStorage = (e: StorageEvent) => {
    if (e.key === KEY) fn()
  }
  window.addEventListener(CHANGED, fn)
  window.addEventListener('storage', onStorage)
  return () => {
    window.removeEventListener(CHANGED, fn)
    window.removeEventListener('storage', onStorage)
  }
}

/** The saved person's id, kept current across windows. Null until someone
 *  has said who they are. */
export function useLocalPersonId(): string | null {
  return useSyncExternalStore(
    onLocalPersonChange,
    () => readLocalPerson()?.id ?? null,
    () => null,
  )
}

/**
 * The change message a write made for this person carries (RFC 0313 3a), or
 * nothing when they have not given a name: the change is then unattributed.
 */
export function personChangeMessage(): { changeMessage?: string } {
  const person = readLocalPerson()
  return person
    ? { changeMessage: JSON.stringify({ authorId: person.id }) }
    : {}
}
